/**
 * In-repo BEM icon catalog for Icon-Liste (and similar).
 * Icons are pure local CSS (`.site-icon--*`) and/or Unicode glyphs — no CDN fonts.
 */

export type SiteIconCategory =
  | 'marks'
  | 'arrows'
  | 'stars'
  | 'shapes'
  | 'business'
  | 'contact'
  | 'media'
  | 'ui'
  | 'nature'
  | 'misc';

export type SiteIconDef = {
  /** Stable id stored in item.icon (e.g. `check`, `star`). */
  id: string;
  /** Short English label for search + picker. */
  label: string;
  category: SiteIconCategory;
  /** Glyph used for preview / fallback when no CSS drawing exists. */
  glyph: string;
  /** True when `site-icons.css` defines `.site-icon--{id}` geometry. */
  css?: boolean;
};

const g = (
  id: string,
  label: string,
  category: SiteIconCategory,
  glyph: string,
  css = false,
): SiteIconDef => ({ id, label, category, glyph, css: css || undefined });

/** Curated catalog (~200). Prefer `id` in storage; legacy single glyphs still parse. */
export const SITE_ICON_CATALOG: SiteIconDef[] = [
  // Marks / status
  g('check', 'Check', 'marks', '✓', true),
  g('check-double', 'Double check', 'marks', '✔', true),
  g('cross', 'Cross', 'marks', '✗', true),
  g('cross-soft', 'Soft cross', 'marks', '✕', true),
  g('plus', 'Plus', 'marks', '+', true),
  g('minus', 'Minus', 'marks', '−', true),
  g('bullet', 'Bullet', 'marks', '•', true),
  g('disc', 'Disc', 'marks', '●', true),
  g('ring', 'Ring', 'marks', '○', true),
  g('square', 'Square', 'marks', '■', true),
  g('square-outline', 'Square outline', 'marks', '□', true),
  g('diamond', 'Diamond', 'marks', '◆', true),
  g('diamond-outline', 'Diamond outline', 'marks', '◇', true),
  g('tri', 'Triangle', 'marks', '▶', true),
  g('tri-outline', 'Triangle outline', 'marks', '▷', true),
  g('spark', 'Spark', 'marks', '✦', true),
  g('spark-outline', 'Spark outline', 'marks', '✧'),
  g('asterisk', 'Asterisk', 'marks', '∗'),
  g('hash', 'Hash', 'marks', '#'),
  g('at', 'At', 'marks', '@'),
  g('percent', 'Percent', 'marks', '%'),
  g('infty', 'Infinity', 'marks', '∞'),
  g('ok', 'OK', 'marks', '☑'),
  g('box', 'Empty box', 'marks', '☐'),
  g('radio-on', 'Radio on', 'marks', '◉'),
  g('radio-off', 'Radio off', 'marks', '◎'),

  // Arrows / direction
  g('arrow-right', 'Arrow right', 'arrows', '→', true),
  g('arrow-left', 'Arrow left', 'arrows', '←', true),
  g('arrow-up', 'Arrow up', 'arrows', '↑', true),
  g('arrow-down', 'Arrow down', 'arrows', '↓', true),
  g('arrow-up-right', 'Arrow up-right', 'arrows', '↗'),
  g('arrow-up-left', 'Arrow up-left', 'arrows', '↖'),
  g('arrow-down-right', 'Arrow down-right', 'arrows', '↘'),
  g('arrow-down-left', 'Arrow down-left', 'arrows', '↙'),
  g('chevron-right', 'Chevron right', 'arrows', '›', true),
  g('chevron-left', 'Chevron left', 'arrows', '‹', true),
  g('chevron-up', 'Chevron up', 'arrows', 'ˆ'),
  g('chevron-down', 'Chevron down', 'arrows', 'ˇ'),
  g('double-right', 'Double right', 'arrows', '»'),
  g('double-left', 'Double left', 'arrows', '«'),
  g('long-right', 'Long right', 'arrows', '⟶'),
  g('long-left', 'Long left', 'arrows', '⟵'),
  g('exchange', 'Exchange', 'arrows', '⇄'),
  g('refresh', 'Refresh', 'arrows', '↻', true),
  g('redo', 'Redo', 'arrows', '↷'),
  g('undo', 'Undo', 'arrows', '↶'),
  g('enter', 'Enter', 'arrows', '⏎'),
  g('export', 'Export', 'arrows', '⇪'),

  // Stars / rating
  g('star', 'Star', 'stars', '★', true),
  g('star-outline', 'Star outline', 'stars', '☆', true),
  g('star-half', 'Star half', 'stars', '⯨'),
  g('heart', 'Heart', 'stars', '♥', true),
  g('heart-outline', 'Heart outline', 'stars', '♡', true),
  g('flame', 'Flame', 'stars', '▴'),
  g('bolt', 'Bolt', 'stars', '⚡', true),
  g('crown', 'Crown', 'stars', '♛'),
  g('medal', 'Medal', 'stars', '🏅'),
  g('trophy', 'Trophy', 'stars', '🏆'),
  g('ribbon', 'Ribbon', 'stars', '🎀'),
  g('sparkles', 'Sparkles', 'stars', '✨'),
  g('sun', 'Sun', 'stars', '☀', true),
  g('moon', 'Moon', 'stars', '☾', true),
  g('cloud', 'Cloud', 'stars', '☁', true),

  // Shapes / geometry
  g('hex', 'Hexagon', 'shapes', '⬡'),
  g('pentagon', 'Pentagon', 'shapes', '⬠'),
  g('circle-half', 'Circle half', 'shapes', '◐'),
  g('quarters', 'Quarters', 'shapes', '◔'),
  g('bars', 'Bars', 'shapes', '☰', true),
  g('grid', 'Grid', 'shapes', '▦', true),
  g('dots', 'Dots', 'shapes', '⋮', true),
  g('dots-h', 'Dots horizontal', 'shapes', '⋯'),
  g('line', 'Line', 'shapes', '—'),
  g('slash', 'Slash', 'shapes', '/'),
  g('backslash', 'Backslash', 'shapes', '\\'),
  g('corner', 'Corner', 'shapes', '⌜'),
  g('frame', 'Frame', 'shapes', '▣'),

  // Business
  g('briefcase', 'Briefcase', 'business', '💼', true),
  g('building', 'Building', 'business', '🏢', true),
  g('chart', 'Chart', 'business', '📊', true),
  g('chart-up', 'Chart up', 'business', '📈'),
  g('chart-down', 'Chart down', 'business', '📉'),
  g('coin', 'Coin', 'business', '◎', true),
  g('wallet', 'Wallet', 'business', '👛'),
  g('cart', 'Cart', 'business', '🛒', true),
  g('tag', 'Tag', 'business', '🏷', true),
  g('gift', 'Gift', 'business', '🎁'),
  g('truck', 'Truck', 'business', '🚚'),
  g('package', 'Package', 'business', '📦', true),
  g('clipboard', 'Clipboard', 'business', '📋'),
  g('calendar', 'Calendar', 'business', '📅', true),
  g('clock', 'Clock', 'business', '🕐', true),
  g('hourglass', 'Hourglass', 'business', '⌛'),
  g('key', 'Key', 'business', '🔑', true),
  g('lock', 'Lock', 'business', '🔒', true),
  g('unlock', 'Unlock', 'business', '🔓'),
  g('shield', 'Shield', 'business', '🛡', true),
  g('badge', 'Badge', 'business', '🔰'),
  g('target', 'Target', 'business', '◎', true),
  g('handshake', 'Handshake', 'business', '🤝'),
  g('users', 'Users', 'business', '👥', true),
  g('user', 'User', 'business', '👤', true),
  g('id', 'ID', 'business', '🪪'),
  g('bank', 'Bank', 'business', '🏦'),
  g('receipt', 'Receipt', 'business', '🧾'),
  g('scale', 'Scale', 'business', '⚖'),
  g('tools', 'Tools', 'business', '🔧', true),
  g('wrench', 'Wrench', 'business', '🔧'),
  g('gear', 'Gear', 'business', '⚙', true),
  g('rocket', 'Rocket', 'business', '🚀'),
  g('lightbulb', 'Lightbulb', 'business', '💡', true),
  g('flag', 'Flag', 'business', '🚩', true),
  g('pin', 'Pin', 'business', '📌', true),
  g('map', 'Map', 'business', '🗺'),
  g('globe', 'Globe', 'business', '🌐', true),
  g('home', 'Home', 'business', '🏠', true),
  g('store', 'Store', 'business', '🏬'),
  g('factory', 'Factory', 'business', '🏭'),

  // Contact / social-ish (local glyphs only)
  g('mail', 'Mail', 'contact', '✉', true),
  g('phone', 'Phone', 'contact', '☎', true),
  g('mobile', 'Mobile', 'contact', '📱'),
  g('chat', 'Chat', 'contact', '💬', true),
  g('message', 'Message', 'contact', '💭'),
  g('send', 'Send', 'contact', '➤', true),
  g('link', 'Link', 'contact', '🔗', true),
  g('unlink', 'Unlink', 'contact', '⛓'),
  g('share', 'Share', 'contact', '⇪'),
  g('bell', 'Bell', 'contact', '🔔', true),
  g('mute', 'Mute', 'contact', '🔕'),
  g('search', 'Search', 'contact', '🔍', true),
  g('filter', 'Filter', 'contact', '⊳'),
  g('info', 'Info', 'contact', 'ℹ', true),
  g('help', 'Help', 'contact', '?', true),
  g('warning', 'Warning', 'contact', '⚠', true),
  g('alert', 'Alert', 'contact', '❗'),
  g('location', 'Location', 'contact', '📍', true),
  g('compass', 'Compass', 'contact', '🧭'),
  g('wifi', 'Wi‑Fi', 'contact', '📶', true),
  g('signal', 'Signal', 'contact', '📡'),

  // Media
  g('image', 'Image', 'media', '🖼', true),
  g('camera', 'Camera', 'media', '📷', true),
  g('video', 'Video', 'media', '🎬', true),
  g('play', 'Play', 'media', '▶', true),
  g('pause', 'Pause', 'media', '❚❚', true),
  g('stop', 'Stop', 'media', '■', true),
  g('record', 'Record', 'media', '●', true),
  g('mic', 'Mic', 'media', '🎤', true),
  g('music', 'Music', 'media', '♪', true),
  g('headphones', 'Headphones', 'media', '🎧'),
  g('book', 'Book', 'media', '📕', true),
  g('bookmark', 'Bookmark', 'media', '🔖', true),
  g('file', 'File', 'media', '📄', true),
  g('folder', 'Folder', 'media', '📁', true),
  g('download', 'Download', 'media', '⬇', true),
  g('upload', 'Upload', 'media', '⬆', true),
  g('print', 'Print', 'media', '🖨'),
  g('edit', 'Edit', 'media', '✎', true),
  g('pen', 'Pen', 'media', '🖊'),
  g('trash', 'Trash', 'media', '🗑', true),
  g('copy', 'Copy', 'media', '❐', true),
  g('paste', 'Paste', 'media', '📋'),
  g('cut', 'Cut', 'media', '✂', true),
  g('attach', 'Attach', 'media', '📎'),
  g('code', 'Code', 'media', '</>', true),
  g('terminal', 'Terminal', 'media', '>_'),
  g('eye', 'Eye', 'media', '👁', true),
  g('eye-off', 'Eye off', 'media', '⊘'),
  g('zoom-in', 'Zoom in', 'media', '🔎'),
  g('zoom-out', 'Zoom out', 'media', '🔍'),

  // UI chrome
  g('menu', 'Menu', 'ui', '☰', true),
  g('close', 'Close', 'ui', '×', true),
  g('expand', 'Expand', 'ui', '⤢'),
  g('collapse', 'Collapse', 'ui', '⤡'),
  g('external', 'External', 'ui', '↗'),
  g('internal', 'Internal', 'ui', '↘'),
  g('layers', 'Layers', 'ui', '▣'),
  g('layout', 'Layout', 'ui', '▥'),
  g('sidebar', 'Sidebar', 'ui', '▥'),
  g('window', 'Window', 'ui', '▢'),
  g('maximize', 'Maximize', 'ui', '⬜'),
  g('minimize', 'Minimize', 'ui', '━'),
  g('drag', 'Drag', 'ui', '⠿', true),
  g('sort', 'Sort', 'ui', '⇅'),
  g('list', 'List', 'ui', '☰'),
  g('kanban', 'Kanban', 'ui', '▤'),
  g('toggle-on', 'Toggle on', 'ui', '◉'),
  g('toggle-off', 'Toggle off', 'ui', '〇'),
  g('power', 'Power', 'ui', '⏻'),
  g('battery', 'Battery', 'ui', '🔋'),
  g('brightness', 'Brightness', 'ui', '☀'),
  g('contrast', 'Contrast', 'ui', '◐'),

  // Nature / lifestyle
  g('leaf', 'Leaf', 'nature', '🍃', true),
  g('tree', 'Tree', 'nature', '🌳'),
  g('flower', 'Flower', 'nature', '❀'),
  g('drop', 'Drop', 'nature', '💧', true),
  g('snow', 'Snow', 'nature', '❄'),
  g('fire', 'Fire', 'nature', '🔥'),
  g('mountain', 'Mountain', 'nature', '⛰'),
  g('wave', 'Wave', 'nature', '〜'),
  g('paw', 'Paw', 'nature', '🐾'),
  g('coffee', 'Coffee', 'nature', '☕'),
  g('food', 'Food', 'nature', '🍽'),
  g('bike', 'Bike', 'nature', '🚲'),
  g('car', 'Car', 'nature', '🚗', true),
  g('plane', 'Plane', 'nature', '✈', true),
  g('train', 'Train', 'nature', '🚆'),
  g('ship', 'Ship', 'nature', '🚢'),
  g('sport', 'Sport', 'nature', '⚽'),
  g('fitness', 'Fitness', 'nature', '💪'),
  g('health', 'Health', 'nature', '✚', true),
  g('lab', 'Lab', 'nature', '⚗'),
  g('atom', 'Atom', 'nature', '⚛'),

  // Misc symbols
  g('quote', 'Quote', 'misc', '❝', true),
  g('quote-end', 'Quote end', 'misc', '❞'),
  g('section', 'Section', 'misc', '§'),
  g('pilcrow', 'Pilcrow', 'misc', '¶'),
  g('degree', 'Degree', 'misc', '°'),
  g('tm', 'Trademark', 'misc', '™'),
  g('copyright', 'Copyright', 'misc', '©'),
  g('registered', 'Registered', 'misc', '®'),
  g('euro', 'Euro', 'misc', '€'),
  g('dollar', 'Dollar', 'misc', '$'),
  g('pound', 'Pound', 'misc', '£'),
  g('yen', 'Yen', 'misc', '¥'),
  g('smile', 'Smile', 'misc', '☺'),
  g('neutral', 'Neutral', 'misc', '😐'),
  g('thumb-up', 'Thumbs up', 'misc', '👍'),
  g('thumb-down', 'Thumbs down', 'misc', '👎'),
  g('clap', 'Clap', 'misc', '👏'),
  g('wave-hand', 'Wave hand', 'misc', '👋'),
  g('peace', 'Peace', 'misc', '✌'),
  g('point', 'Point', 'misc', '☝'),
  g('magic', 'Magic', 'misc', '🪄'),
  g('puzzle', 'Puzzle', 'misc', '🧩'),
  g('seedling', 'Seedling', 'misc', '🌱'),
  g('recycle', 'Recycle', 'misc', '♻', true),
  g('accessibility', 'Accessibility', 'misc', '♿'),
  g('language', 'Language', 'misc', '文'),
  g('translate', 'Translate', 'misc', 'あ'),
  g('verse', 'Verse', 'misc', '⁂'),
  g('ellipsis', 'Ellipsis', 'misc', '…'),
  g('nbsp', 'Dot leader', 'misc', '·'),
  g('pipe', 'Pipe', 'misc', '|'),
  g('tilde', 'Tilde', 'misc', '~'),
  g('caret', 'Caret', 'misc', '^'),
];

const BY_ID = new Map(SITE_ICON_CATALOG.map((icon) => [icon.id, icon]));
const BY_GLYPH = new Map<string, SiteIconDef>();
for (const icon of SITE_ICON_CATALOG) {
  if (!BY_GLYPH.has(icon.glyph)) BY_GLYPH.set(icon.glyph, icon);
}

/** Legacy single-char prefixes still accepted in stored list lines. */
export const SITE_ICON_GLYPH_PREFIX_RE = /^[✓✗→←↑↓•●○■□◆◇▶▷»«✦✧★☆♥♡⚡☀☾☁✉☎✔✕＋+−–☑☐◉◎⌘⚙🔍📍🏠🔒🔑🛡💡✉]/;

export const SITE_ICON_CATEGORIES: SiteIconCategory[] = [
  'marks',
  'arrows',
  'stars',
  'shapes',
  'business',
  'contact',
  'media',
  'ui',
  'nature',
  'misc',
];

export function getSiteIconById(id: string | null | undefined): SiteIconDef | undefined {
  if (!id) return undefined;
  return BY_ID.get(id.trim().toLowerCase());
}

export function resolveSiteIcon(raw: string | null | undefined): SiteIconDef {
  const value = (raw || '').trim();
  if (!value) return BY_ID.get('check')!;
  const byId = BY_ID.get(value.toLowerCase());
  if (byId) return byId;
  const byGlyph = BY_GLYPH.get(value) || BY_GLYPH.get([...value][0] || '');
  if (byGlyph) return byGlyph;
  return {
    id: value,
    label: value,
    category: 'misc',
    glyph: [...value][0] || '✓',
  };
}

export function isNamedSiteIconId(value: string | null | undefined): boolean {
  if (!value) return false;
  return BY_ID.has(value.trim().toLowerCase());
}

export function filterSiteIcons(
  query: string,
  category: SiteIconCategory | 'all' = 'all',
): SiteIconDef[] {
  const q = query.trim().toLowerCase();
  return SITE_ICON_CATALOG.filter((icon) => {
    if (category !== 'all' && icon.category !== category) return false;
    if (!q) return true;
    return (
      icon.id.includes(q) ||
      icon.label.toLowerCase().includes(q) ||
      icon.glyph.includes(q) ||
      icon.category.includes(q)
    );
  });
}

/** Format icon token for list-line serialization (`@check` or glyph). */
export function formatSiteIconToken(icon: string | null | undefined): string {
  const value = (icon || '').trim() || 'check';
  if (isNamedSiteIconId(value)) return `@${value.toLowerCase()}`;
  // Keep a literal single glyph when the editor still stores one.
  if ([...value].length === 1) return value;
  const resolved = resolveSiteIcon(value);
  if (isNamedSiteIconId(resolved.id)) return `@${resolved.id}`;
  return resolved.glyph;
}

/**
 * Parse a leading icon token from a list line.
 * Supports `@check text`, legacy `✓ text`, or plain text (default check).
 */
export function parseSiteIconPrefix(line: string): { icon: string; text: string } {
  const trimmed = line.trim();
  if (!trimmed) return { icon: 'check', text: '' };

  const named = trimmed.match(/^@([a-z0-9][a-z0-9-]{0,48})\b\s*(.*)$/i);
  if (named) {
    const id = named[1].toLowerCase();
    return {
      icon: isNamedSiteIconId(id) ? id : named[1],
      text: (named[2] || '').trim(),
    };
  }

  const first = [...trimmed][0] || '';
  if (SITE_ICON_GLYPH_PREFIX_RE.test(first) || BY_GLYPH.has(first)) {
    const mapped = BY_GLYPH.get(first);
    return {
      icon: mapped?.id || first,
      text: trimmed.slice(first.length).trim(),
    };
  }

  return { icon: 'check', text: trimmed };
}
